import { supabase } from '../lib/supabase'
import {
  FinanceiroPastaJaProcessadaError,
  type AppendComissoesIcatuRowsInput,
  type BeginComissoesIcatuPastaInput,
  type CompleteComissoesIcatuPastaInput,
  type FinanceiroComissaoRow,
  type FinanceiroComissoesIcatuPasta,
} from '../types/financeiro'

const BATCH_SIZE = 500

const FOLDERS_TABLE = 'finan_icatu_commission_folders'
const ROWS_TABLE = 'finan_icatu_commissions'

function rowToInsert(
  row: FinanceiroComissaoRow,
  folderId: string,
  uploadedBy: string | null
) {
  return {
    folder_id: folderId,
    folder_name: row.pasta,
    file_name: row.arquivo,
    codigo_corretor: row.codigoCorretor,
    nome_corretor: row.nomeCorretor,
    cpf_cnpj: row.cpfCnpj,
    apolice: row.apolice,
    numero_sub: row.numeroSub,
    modulo: row.modulo,
    estipulante: row.estipulante,
    sub_estipulante: row.subEstipulante,
    fatura: row.fatura,
    competencia: row.competencia,
    valor_apropriado: row.valorApropriado,
    tipo_comissao: row.tipoComissao,
    parcela_inicial: row.parcelaInicial,
    numero_grupo_cobertura: row.numeroGrupoCobertura,
    valor_base: row.valorBase,
    percentual_comissao: row.percentualComissao,
    valor_comissao: row.valorComissao,
    data_emissao: row.dataEmissao,
    data_apropriacao: row.dataApropriacao,
    valor_premio_bruto: row.valorPremioBruto,
    valor_premio_tarifario: row.valorPremioTarifario,
    data_movimento: row.dataMovimento,
    data_agendamento: row.dataAgendamento,
    data_pagamento: row.dataPagamento,
    data_pagamento_efetivo: row.dataPagamentoEfetivo,
    status_recibo: row.statusRecibo,
    nr_cobranca: row.nrCobranca,
    uploaded_by: uploadedBy,
  }
}

export const financeiroService = {
  /** Pastas finalizadas (status = complete). */
  async listPastasProcessadas(): Promise<FinanceiroComissoesIcatuPasta[]> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('*')
      .eq('status', 'complete')
      .order('folder_name', { ascending: true })

    if (error) throw error
    return (data ?? []) as FinanceiroComissoesIcatuPasta[]
  },

  async isPastaProcessada(pastaNome: string): Promise<boolean> {
    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .select('id')
      .eq('folder_name', pastaNome)
      .eq('status', 'complete')
      .maybeSingle()

    if (error) throw error
    return !!data
  },

  /**
   * Remove pastas `processing` órfãs do próprio usuário (ex.: crash antigo).
   * Não apaga jobs recentes nem de outros usuários — evita matar import em outra aba/sessão.
   * CASCADE apaga linhas parciais. Retentativa imediata da mesma pasta é feita em `beginPasta`.
   */
  async cleanupIncompletePastas(userId: string | null | undefined): Promise<number> {
    if (!userId) return 0

    const staleBefore = new Date(Date.now() - 60 * 60 * 1000).toISOString()

    const { data, error } = await supabase
      .from(FOLDERS_TABLE)
      .delete()
      .eq('status', 'processing')
      .eq('processed_by', userId)
      .lt('processed_at', staleBefore)
      .select('id')

    if (error) throw error
    return data?.length ?? 0
  },

  /**
   * Abre a pasta com status=processing. Falha se já existir complete com o mesmo nome.
   */
  async beginPasta(
    input: BeginComissoesIcatuPastaInput
  ): Promise<FinanceiroComissoesIcatuPasta> {
    const pastaNome = input.pastaNome.trim()
    if (!pastaNome) throw new Error('Nome da pasta é obrigatório.')

    const already = await this.isPastaProcessada(pastaNome)
    if (already) throw new FinanceiroPastaJaProcessadaError(pastaNome)

    // Limpa sobra processing com o mesmo nome (unique em folder_name)
    const { error: cleanupError } = await supabase
      .from(FOLDERS_TABLE)
      .delete()
      .eq('folder_name', pastaNome)
      .eq('status', 'processing')

    if (cleanupError) throw cleanupError

    const { data: pasta, error: pastaError } = await supabase
      .from(FOLDERS_TABLE)
      .insert({
        folder_name: pastaNome,
        year: input.ano,
        root_folder_name: input.rootFolderName,
        files_ok: 0,
        files_error: 0,
        row_count: 0,
        status: 'processing',
        processed_by: input.userId,
        processed_at: new Date().toISOString(),
      })
      .select('*')
      .single()

    if (pastaError) {
      if (pastaError.code === '23505') {
        throw new FinanceiroPastaJaProcessadaError(pastaNome)
      }
      throw pastaError
    }

    return pasta as FinanceiroComissoesIcatuPasta
  },

  /** Insere linhas de um arquivo em lotes; não marca a pasta como completa. */
  async appendRows(input: AppendComissoesIcatuRowsInput): Promise<number> {
    if (input.rows.length === 0) return 0

    const payloads = input.rows.map((r) =>
      rowToInsert(r, input.folderId, input.userId)
    )
    const fileTotal = payloads.length
    let savedInFile = 0

    input.onProgress?.({
      pastaNome: input.pastaNome,
      savedRows: input.savedRowsBefore,
      totalRows: input.savedRowsBefore + fileTotal,
    })

    for (let i = 0; i < payloads.length; i += BATCH_SIZE) {
      const chunk = payloads.slice(i, i + BATCH_SIZE)
      const { error: rowsError } = await supabase.from(ROWS_TABLE).insert(chunk)
      if (rowsError) throw rowsError
      savedInFile += chunk.length
      input.onProgress?.({
        pastaNome: input.pastaNome,
        savedRows: input.savedRowsBefore + savedInFile,
        totalRows: input.savedRowsBefore + fileTotal,
      })
    }

    return savedInFile
  },

  async completePasta(input: CompleteComissoesIcatuPastaInput): Promise<void> {
    const { error } = await supabase
      .from(FOLDERS_TABLE)
      .update({
        status: 'complete',
        files_ok: input.arquivosOk,
        files_error: input.arquivosErro,
        row_count: input.rowCount,
        processed_at: new Date().toISOString(),
      })
      .eq('id', input.folderId)

    if (error) throw error
  },

  /** Apaga pasta em processing (e linhas via CASCADE) após falha. */
  async abortPasta(folderId: string): Promise<void> {
    const { error } = await supabase.from(FOLDERS_TABLE).delete().eq('id', folderId)
    if (error) throw error
  },
}
